import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TrendingDown, Wallet, ShoppingCart, Cog, Home, IndianRupee, ArrowRight, FileDown, ScrollText } from "lucide-react";
import { useNavigate } from "react-router-dom";

const KIND_ICON = {
  asset_emi:  { icon: Cog, color: "#8A6600", label: "Asset EMI" },
  asset_rent: { icon: Home, color: "#8A6600", label: "Asset Rent" },
  po_due:     { icon: ShoppingCart, color: "#C9A24B", label: "PO Due" },
  payroll:    { icon: Wallet, color: "#0F5E39", label: "Payroll" },
};

export default function CashFlowPage() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cash-flow/forecast?days=${d}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  const maxDay = useMemo(() => {
    if (!data?.daily) return 1;
    return Math.max(1, ...data.daily.map((d) => d.total));
  }, [data]);

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="cash-flow-page">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-[#083F22] text-white flex items-center justify-center"><TrendingDown size={18}/></div>
        <div className="flex-1">
          <div className="font-serif text-2xl">Cash-Flow Forecast</div>
          <div className="text-xs text-[#4A5A50]">Projected outflow: Asset EMI + rent + PO dues + Payroll</div>
        </div>
        <div className="inline-flex rounded-full bg-white border border-[#E4DFCF] p-1 text-xs" data-testid="cash-flow-range">
          {[30, 60, 90, 180].map((n) => (
            <button key={n} onClick={() => setDays(n)}
              className={`px-3 py-1 rounded-full ${days === n ? "bg-[#0F5E39] text-white" : "text-[#4A5A50]"}`}
              data-testid={`cash-flow-days-${n}`}>
              {n} days
            </button>
          ))}
        </div>
        <Button size="sm"
          className="rounded-full bg-[#C9A24B] hover:bg-[#B88C34] text-[#0F1E15]"
          onClick={async () => { try { await openPdf(`/api/reports/cash-flow.pdf?days=${days}`); } catch { toast.error("PDF failed"); } }}
          data-testid="cash-flow-share-pdf"
        >
          <FileDown size={14} className="mr-1"/>Share PDF
        </Button>
        <Button size="sm" variant="outline"
          className="rounded-full"
          onClick={async () => { try { await openPdf(`/api/reports/weekly-digest.pdf`); } catch { toast.error("PDF failed"); } }}
          data-testid="cash-flow-weekly-digest"
        >
          <ScrollText size={14} className="mr-1"/>Weekly digest
        </Button>
        <Button size="sm" variant="outline"
          className="rounded-full border-[#C9A24B] text-[#8A6600] hover:bg-[#FFF6E1]"
          onClick={async () => {
            try {
              const { data } = await api.post("/share/weekly-digest");
              await navigator.clipboard?.writeText(data.url);
              toast.success(`Signed link copied — expires ${new Date(data.expires_at).toLocaleDateString("en-IN")}`);
            } catch (e) { toast.error("Failed to create share link"); }
          }}
          data-testid="share-digest-link-btn"
        >
          Copy 7-day link
        </Button>
      </div>

      {loading && <div className="text-sm text-[#4A5A50] italic">Loading…</div>}

      {data && (
        <>
          <Card><CardContent className="p-4 grid md:grid-cols-5 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Total outflow ({days}d)</div>
              <div className="font-serif text-3xl text-[#0F5E39]" data-testid="cash-flow-total">₹{data.total_outflow.toLocaleString("en-IN")}</div>
            </div>
            {Object.entries(data.by_kind).map(([k, v]) => {
              const meta = KIND_ICON[k];
              const Icon = meta?.icon || IndianRupee;
              return (
                <div key={k} className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ background: `${meta?.color || "#0F5E39"}22`, color: meta?.color || "#0F5E39" }}><Icon size={14}/></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">{meta?.label || k}</div>
                    <div className="font-serif text-lg">₹{Number(v).toLocaleString("en-IN")}</div>
                  </div>
                </div>
              );
            })}
          </CardContent></Card>

          {data.daily.length === 0 ? (
            <p className="text-sm text-[#4A5A50] italic" data-testid="cash-flow-empty">No scheduled outflows in this window.</p>
          ) : (
            <div className="space-y-2">
              {data.daily.map((d) => (
                <Card key={d.date}><CardContent className="p-3" data-testid={`cash-flow-day-${d.date}`}>
                  <div className="flex items-center gap-2">
                    <div className="font-serif text-sm w-28">{new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
                    <div className="flex-1">
                      <div className="h-2 rounded-full bg-[#F1EDDF] overflow-hidden">
                        <div className="h-full bg-[#0F5E39]" style={{ width: `${(d.total / maxDay) * 100}%` }}/>
                      </div>
                    </div>
                    <div className="font-serif text-lg text-[#083F22] w-32 text-right">₹{d.total.toLocaleString("en-IN")}</div>
                  </div>
                  <div className="mt-2 space-y-1">
                    {d.items.map((it, i) => {
                      const meta = KIND_ICON[it.kind];
                      const Icon = meta?.icon || IndianRupee;
                      return (
                        <div key={i} className="flex items-center gap-2 text-xs text-[#4A5A50] pl-28">
                          <Icon size={12} style={{ color: meta?.color || "#0F5E39" }}/>
                          <span className="flex-1 truncate">{it.label}</span>
                          <span className="font-mono">₹{Number(it.amount).toLocaleString("en-IN")}</span>
                          {it.ref?.path && it.ref.path !== "/" && (
                            <button onClick={() => nav(it.ref.path)} className="text-[#0F5E39] hover:text-[#083F22]"><ArrowRight size={12}/></button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent></Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
